import ProfileAvatar from 'components/ProfileAvatar';
import './ProfileMenu.css';
import Points from 'components/Points';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton, useTheme } from '@mui/material';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { logout } from 'store/services/auth/authSlice';
import { meApi } from 'store/services/user/meApi';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { tradeApi } from 'store/services/trade/tradeApi';
import { cardsSearchApi } from 'store/services/cards/search/cardsSearchApi';
import { authApi } from 'store/services/auth/authApi';
import { AssetUrls } from 'config/assets';

export const ProfileMenu = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth?.token);
  const user = useSelector((state: RootState) => state.auth?.user);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user data if we have a token OR user (cookie mode)
  const { isLoading, data } = useGetMeQuery(undefined, {
    skip: !token && !user, // Skip only if neither token nor user exists
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleLogout = () => {
    // Clear all RTK Query caches to prevent data leakage between accounts
    dispatch(meApi.util.resetApiState());
    dispatch(haveListApi.util.resetApiState());
    dispatch(wantListApi.util.resetApiState());
    dispatch(tradeApi.util.resetApiState());
    dispatch(cardsSearchApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());

    // Clear auth token
    dispatch(logout());

    // Navigate to welcome page
    navigate('/welcome');
  };

  return (
    <div className="profile-menu-container" ref={dropdownRef}>
      <div
        className="profile-menu"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{ cursor: 'pointer' }}
      >
        <ProfileAvatar
          isLoading={isLoading}
          profilePictureUri={data?.avatarUrl || undefined}
          username={data?.username || undefined}
        />
        <div className="profile-menu-account-details">
          {isLoading ? (
            <Skeleton
              variant="rounded"
              sx={{ backgroundColor: theme.colors.grey, borderRadius: 12 }}
              width={68}
              height={20}
            />
          ) : (
            <div className="profile-menu-account-details-username">
              {data?.username ?? ''}
            </div>
          )}
          <Points points={data?.points} isLoading={isLoading} />
        </div>
        <div className="profile-menu-arrow-menu">
          <FontAwesomeIcon
            icon={'chevron-down'}
            width={10}
            color={theme.colors.lighterGrey}
            style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          />
        </div>
      </div>

      {isDropdownOpen && (
        <div className="profile-menu-dropdown">
          <div
            className="profile-menu-dropdown-item"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/settings');
              setIsDropdownOpen(false);
            }}
          >
            <span>Settings</span>
          </div>
          <div
            className="profile-menu-dropdown-item"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/transaction-history');
              setIsDropdownOpen(false);
            }}
          >
            <span>Ledger</span>
          </div>
          <div
            className="profile-menu-dropdown-item"
            onClick={handleLogout}
          >
            <img
              src={AssetUrls.icons.dropdowns.arrowRight}
              alt="logout-icon"
              style={{ width: 16, height: 16 }}
            />
            <span>Logout</span>
          </div>
        </div>
      )}
    </div>
  );
};
